import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { type StreamScopedPathParams, streamScopedPathSchema } from './shared'

/**
 * The schema for a coordinate pair. Longitude is the first element, latitude is the second.
 */
const coordSchema = z.tuple([z.number(), z.number()])

/**
 * Location of a tractor at a given record, as well as a timestamp for when the tractor was at that location.
 */
const responseSchema = z.object({
  /**
   * The record's id in the .2020 file, which the record-id tile layer paints. The `record` query
   * parameter is an index into the stream's records, not this id.
   */
  record_id: z.number(),
  /**
   * The coordinates of the hitch of the tractor
   */
  hitch: coordSchema,
  /**
   * The coordinates of the left and right bars of each implement attached to the tractor (may be more than one bar)
   */
  bars: z.array(
    z.object({
      left: coordSchema,
      right: coordSchema
    })
  ),
  /**
   * The direction the tractor is facing in degrees (north is 0, east is 90, etc.)
   */
  heading: z.number(),
  /**
   * The time the tractor was at this location
   */
  time: z.coerce.date()
})

export type TractorPosition = z.infer<typeof responseSchema>

interface Params extends StreamScopedPathParams {
  record: number
  lastRecordId?: number
}

const pathFor = (p: Params) => {
  const { record, lastRecordId } = p
  return path(`${p.requestType}/streams/${p.orgCode}/${p.streamId}/diagnostics/location`, {
    record,
    lastRecordId
  })
}

export const locationApi = defineApi({
  pathSchema: streamScopedPathSchema,
  querySchema: z.object({
    record: z.number(),
    lastRecordId: z.number().optional()
  }),
  pathFor,
  queryKey: ({ orgCode, streamId, requestType, record, lastRecordId }) => [
    'tractor-location',
    requestType,
    orgCode,
    streamId,
    record,
    lastRecordId
  ],
  name: 'GetTractorLocation',
  description:
    'Get the GPS coordinates and heading of the tractor at a specific record in the stream',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: responseSchema,
  ttlSeconds: Number.POSITIVE_INFINITY // The location data is immutable
})

export const selectLocation = {
  getTime: (response: TractorPosition) => response.time
}
